import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import { Modal } from '../../../components/modal/Modal';
import { Button } from '../../../components/buttons/Button';
import { validUrl, normalizeUrl } from '../../../common/urlValid';
import {
    HiOutlineGlobeAlt,
    HiOutlineClipboardDocument,
    HiOutlineX,
} from '../../../icons';

export const ChatUrlModal = ({
    open,
    onClose,
    onSubmit,
    loading = false,
}) => {
    const cancelButtonRef = useRef(null);
    const inputRef = useRef(null);
    const [url, setUrl] = useState('');

    useEffect(() => {
        if (!open) {
            setUrl('');
            return;
        }
        const timer = window.setTimeout(() => inputRef.current?.focus(), 120);
        return () => window.clearTimeout(timer);
    }, [open]);

    const handlePaste = useCallback(async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (text?.trim()) {
                setUrl(text.trim());
                inputRef.current?.focus();
            }
        } catch {
            toast.error('No se pudo pegar desde el portapapeles.');
        }
    }, []);

    const handleClear = useCallback(() => {
        setUrl('');
        inputRef.current?.focus();
    }, []);

    const handleSubmit = useCallback(() => {
        const normalized = normalizeUrl(url);
        if (!validUrl(normalized)) {
            toast.error('Ingresa un enlace válido (http o https).');
            return;
        }
        onSubmit?.(normalized);
    }, [url, onSubmit]);

    return (
        <Modal open={open} setOpen={onClose} cancelButtonRef={cancelButtonRef}>
            <div className="chat-url-modal">
                <Dialog.Title as="h3" className="chat-url-modal__title">
                    Compartir enlace
                </Dialog.Title>
                <p className="chat-url-modal__description">
                    Pega o escribe la URL que quieres enviar al chat del curso.
                </p>

                <div className="chat-url-modal__field">
                    <div className="chat-url-modal__input-shell">
                        <span className="chat-url-modal__globe" aria-hidden="true">
                            <HiOutlineGlobeAlt className="chat-url-modal__globe-icon" />
                        </span>
                        <input
                            ref={inputRef}
                            type="url"
                            inputMode="url"
                            autoComplete="off"
                            className="chat-url-modal__input"
                            placeholder="Ingresa la URL del sitio"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSubmit();
                                }
                            }}
                        />
                        <div className="chat-url-modal__input-actions">
                            <button
                                type="button"
                                className="chat-url-modal__icon-btn"
                                onClick={handlePaste}
                                title="Pegar enlace"
                                aria-label="Pegar enlace"
                            >
                                <HiOutlineClipboardDocument className="chat-url-modal__icon-btn-svg" aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                className="chat-url-modal__icon-btn"
                                onClick={handleClear}
                                title="Limpiar"
                                aria-label="Limpiar enlace"
                                disabled={!url}
                            >
                                <HiOutlineX className="chat-url-modal__icon-btn-svg" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                    <p className="chat-url-modal__hint">Pega el enlace de tu sitio web</p>
                </div>

                <div className="chat-url-modal__footer">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => onClose(false)}
                        disabled={loading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        loading={loading}
                        onClick={handleSubmit}
                        disabled={!url.trim()}
                    >
                        Enviar enlace
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
